import type { DebugLogger } from 'lib/debugLogger';

export type ConditionWithData = { [x: string]: any; check: boolean };
export type SimpleConditionsWithData = Record<string, boolean | ConditionWithData | undefined>;
export type ConditionsWithData = Record<string, boolean | (() => boolean) | ConditionWithData | (() => ConditionWithData)>;

const resolveCondition = (value: ConditionsWithData[string]): boolean | ConditionWithData => {
    if (typeof value === 'function') {
        return value?.();
    }
    return value;
};

export const enum ConditionsCheckType {
    FirstFalse = 'FirstFalse',
    FirstTrue = 'FirstTrue',
}

export const checkConditions = (
    conditions: ConditionsWithData,
    mode: ConditionsCheckType = ConditionsCheckType.FirstFalse
): { result: boolean; resolvedConditions: SimpleConditionsWithData } => {
    const resolvedConditions: SimpleConditionsWithData = Object.keys(conditions).reduce((acc, curr) => {
        acc[curr] = undefined;
        return acc;
    }, {});

    for (const [conditionName, conditionValue] of Object.entries(conditions)) {
        const conditionResult = resolveCondition(conditionValue);
        resolvedConditions[conditionName] = conditionResult;
        let conditionCheckResult = false;
        if (typeof conditionResult === 'object' && conditionResult !== null) {
            conditionCheckResult = conditionResult.check;
        } else {
            conditionCheckResult = Boolean(conditionResult);
        }
        if (!conditionCheckResult && mode === ConditionsCheckType.FirstFalse) {
            return { result: false, resolvedConditions };
        }
        if (conditionCheckResult && mode === ConditionsCheckType.FirstTrue) {
            return { result: false, resolvedConditions };
        }
    }

    return { result: true, resolvedConditions };
};

export const getCheckAllConditions = (logger: DebugLogger, mode: ConditionsCheckType = ConditionsCheckType.FirstFalse) => {
    return (message: string, conditions: ConditionsWithData) => {
        const { result, resolvedConditions } = checkConditions(conditions, mode);

        logger.switch(result, message, resolvedConditions);

        return result;
    };
};

export class ConditionsChecker {
    private list: Record<string, boolean>;
    private onFail: (conditionsScope: string, list: Record<string, boolean>) => void;

    constructor(onFail: (conditionsScope: string, list: Record<string, boolean>) => void) {
        this.list = {};
        this.onFail = onFail;
    }

    check(conditionsScope: string): boolean {
        const result = Object.values(this.list).includes(true);
        if (result) {
            this.onFail(conditionsScope, this.list);
        }
        return !result;
    }

    add(newConditions: Record<string, boolean>): void {
        this.list = {
            ...this.list,
            ...newConditions,
        };
    }

    addWithCheck(conditionsScope: string, newConditions: Record<string, boolean>): boolean {
        this.add(newConditions);
        return this.check(conditionsScope);
    }

    getList(): Record<string, boolean> {
        return this.list;
    }
}
